import type { GQL } from "@binale-tech/shared";

import type { MessageId } from "@domain/intl/useTranslation";

import type { AgentContext, AgentDetailItem, AgentIO } from "../runner/types";

export const AI_POLL_MS = 1000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const isSettled = (doc: GQL.IDocument | undefined, errorCountsFrom: number): boolean => {
    const status = doc?.aiStatus?.status;
    if (status === "done") return true;
    return status === "error" && Date.now() >= errorCountsFrom;
};

export interface AiProgressOutcome {
    done: number;
    failed: number;
    /** Ids with no document or no aiStatus after the grace period: AI was never started for them. */
    notStarted: number;
    /** Ids still being read when the wait ended (cancel or stall). */
    running: number;
    total: number;
    /** True when the modal was closed before every document settled. */
    cancelled: boolean;
    /** True when nothing changed for STALL_MS; the still running ids continue on the server. */
    stalled: boolean;
    /** The ids behind the counts above. */
    doneIds: string[];
    failedIds: string[];
    notStartedIds: string[];
    runningIds: string[];
}

/** One detail item per id: read, failed, never started or still being read. */
export const aiDetails = (outcome: AiProgressOutcome): AgentDetailItem[] => {
    const item = (id: string, group: AgentDetailItem["group"], textId: MessageId): AgentDetailItem => ({
        group,
        ref: { kind: "document", id },
        outcome: { textId },
    });
    return [
        ...outcome.doneIds.map((id) => item(id, "done", "app.agent.details.ai.read")),
        ...outcome.failedIds.map((id) => item(id, "refused", "app.agent.details.ai.failed")),
        ...outcome.notStartedIds.map((id) => item(id, "untouched", "app.agent.details.ai.notStarted")),
        ...outcome.runningIds.map((id) => item(id, "untouched", "app.agent.details.ai.running")),
    ];
};

export const NOT_STARTED_GRACE_MS = 30_000;
export const STALL_MS = 600_000;

/**
 * Polls the live documents map until every id is done, failed or never started, or the run is cancelled or stalls.
 * `errorGraceMs` keeps a stale error from before a retry from counting as settled.
 */
export const waitForAi = async (
    ctx: AgentContext,
    io: AgentIO,
    ids: string[],
    progressId: string,
    errorGraceMs = 0,
): Promise<AiProgressOutcome> => {
    const startedAt = Date.now();
    const errorCountsFrom = startedAt + errorGraceMs;
    const measure = () => {
        const docs = ctx.documents();
        const doneIds: string[] = [];
        const failedIds: string[] = [];
        const notStartedIds: string[] = [];
        const runningIds: string[] = [];
        for (const id of ids) {
            const doc = docs.get(id);
            if (!doc?.aiStatus) {
                if (Date.now() - startedAt >= NOT_STARTED_GRACE_MS) notStartedIds.push(id);
                else runningIds.push(id);
            } else if (isSettled(doc, errorCountsFrom)) {
                if (doc.aiStatus.status === "done") doneIds.push(id);
                else failedIds.push(id);
            } else {
                runningIds.push(id);
            }
        }
        return { doneIds, failedIds, notStartedIds, runningIds };
    };

    let lastSettled = -1;
    let lastChange = startedAt;
    for (;;) {
        await sleep(AI_POLL_MS);
        const lists = measure();
        const done = lists.doneIds.length;
        const failed = lists.failedIds.length;
        const notStarted = lists.notStartedIds.length;
        const settledCount = done + failed + notStarted;
        io.update(progressId, { progress: { done: settledCount, total: ids.length } });
        if (settledCount !== lastSettled) {
            lastSettled = settledCount;
            lastChange = Date.now();
        }
        const result = { done, failed, notStarted, running: ids.length - settledCount, total: ids.length, ...lists };
        if (settledCount === ids.length) return { ...result, cancelled: false, stalled: false };
        if (io.isCancelled()) return { ...result, cancelled: true, stalled: false };
        if (Date.now() - lastChange >= STALL_MS) return { ...result, cancelled: false, stalled: true };
    }
};
